import { Heading, HeadingLevel } from "@ariakit/react";
import { MiddotSeparated, useIsInstalled } from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import { Fragment } from "react";
import { Link, useParams } from "wouter";
import { useInvokeClient } from "../../client.ts";
import { trackEvent, type TrackedEvent } from "../../lib/fathom.tsx";
import { CheckAccess } from "../CheckAccess/CheckAccess.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import { Page } from "../Page/Page.tsx";
import { FailureView } from "../failures/FailureView.tsx";
import { conjunction, FeatureEnum } from "../fmt.ts";
import { Divider } from "../forms/Divider.tsx";
import contentUrl from "./content.png";
import discord from "./discord.svg";
import patreon from "./patreon.svg";
import { page, section, socials } from "./style.css.ts";
import youtube from "./youtube.svg";

export function CheckoutSuccessPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const isInstalled = useIsInstalled();
  const [result] = useInvokeClient("getCheckoutSession", [sessionId]);

  if (result.isPending) {
    return (
      <Page title="" backHref="~/" centerChildren>
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page title="" backHref="~/" centerChildren>
        <FailureView
          failure={result.failure}
          messages={{
            409: {
              title: "Payment not yet processed",
              description: `Our payments provider has not yet confirmed that a payment was received. Please check back later, or contact support if this issue persists.`,
            },
          }}
        />
      </Page>
    );
  }

  const { products, orderRef } = result.value;
  const someMissingDownloads = products.some((p) => !p.downloadUrl);
  const unlocks = new Set(
    products
      .flatMap((product) => product.unlocks?.map((u) => u.feature) ?? [])
      .filter((feature) => FeatureEnum.has(feature))
      .map((feature) => FeatureEnum.get(feature)),
  );

  return (
    <Page title="Success!" backHref="~/">
      {/* Re-run the check access call to make sure that if new unlocks were
      added, they show up in the app. This is important in case Stripe was too
      slow notifying us that a purchase happened via webhooks and it was the
      session check call that resulted in the purchased being processed. */}
      <CheckAccess />

      <div className={page.container}>
        <div className={page.paper}>
          <div className={page.header}>
            <div className={page.overline}>Got the cash! Thanks!</div>
            <Heading className={page.heading}>What next?</Heading>
            <div className={page.orderInfo}>
              Your payment has been processed successfully.
              <br />
              Order reference: <span className={page.orderRef}>{orderRef}</span>
            </div>
          </div>

          <HeadingLevel>
            <div className={page.sections}>
              <div>
                <Heading className={section.heading}>
                  Download your stuff
                </Heading>

                <p className={section.info}>
                  You are all set to download your new stuff! Your purchases are
                  saved to your account, so you can always get back to them
                  later from the app.
                </p>

                {someMissingDownloads && (
                  <p className={section.info}>
                    <strong>⚠️ Warning</strong>: Some digital products below are
                    missing download links. This is probably an issue with our
                    service. Our team has been automatically notified, but if
                    you don&apos;t hear from us soon, please get in touch with
                    support@indietabletop.club. Sorry about this!
                  </p>
                )}

                <div className={section.downloads}>
                  {products.map((product) => {
                    return (
                      <Fragment key={product.id}>
                        <Divider />

                        <div className={section.productRow}>
                          <div>
                            <div className={section.productName}>
                              {product.name}
                            </div>

                            <MiddotSeparated className={section.productInfo}>
                              {product.downloadFileVersion && (
                                <span>
                                  Version {product.downloadFileVersion}
                                </span>
                              )}
                              <span>{product.downloadFileSize}</span>
                            </MiddotSeparated>
                          </div>

                          <a
                            className={section.downloadLink}
                            href={product.downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                          >
                            Download
                          </a>
                        </div>
                      </Fragment>
                    );
                  })}
                  <Divider />
                </div>
              </div>

              {unlocks.size > 0 && (
                <div className={section.container}>
                  <div>
                    <Heading className={section.heading}>
                      Build your gang
                    </Heading>

                    <p className={section.info}>
                      Your purchase has unlocked in-app access to{" "}
                      {conjunction(Array.from(unlocks))}. All related content
                      will now be visible all across the app. Snazzy!
                    </p>

                    {!isInstalled && (
                      <p className={section.info}>
                        <em>
                          Pssst! Did you know you can install the app on any
                          modern device to make it extra easy to use?{" "}
                          <Link
                            href="~/docs/help/installation"
                            className={interactiveText}
                          >
                            Learn more
                          </Link>
                          .
                        </em>
                      </p>
                    )}
                    <Link className={section.gangBuilderLink} href="~/gangs">
                      Go to Gang Builder
                    </Link>
                  </div>

                  <img
                    src={contentUrl}
                    alt=""
                    className={section.illustration}
                  />
                </div>
              )}

              <div>
                <div>
                  <Heading className={section.heading}>Meet other Gits</Heading>

                  <p className={section.info}>
                    Want to hang out with other Space Gits players? Chat about
                    the game? Show off your gangs? Join Mike&apos;s Planet
                    Smasher Games communities.
                  </p>
                </div>

                <div className={section.externalLinks}>
                  <Socials />
                </div>
              </div>
            </div>
          </HeadingLevel>
        </div>
      </div>
    </Page>
  );
}

function SocialLink(props: {
  href: string;
  action: string;
  description: string;
  logoSrc: string;
  event: TrackedEvent;
}) {
  return (
    <div className={socials.item}>
      <a
        href={props.href}
        target="_blank"
        rel="noreferrer noopener"
        onClick={() => void trackEvent(props.event)}
        style={{ display: "contents" }}
      >
        <img src={props.logoSrc} alt="" className={socials.icon} />
      </a>

      <div className={socials.info}>{props.description}</div>

      <a
        href={props.href}
        target="_blank"
        rel="noreferrer noopener"
        onClick={() => void trackEvent(props.event)}
        className={socials.action}
      >
        {props.action}
      </a>
    </div>
  );
}

function Socials() {
  return (
    <div className={socials.container}>
      <SocialLink
        href="https://planetsmashergames.com/discord"
        logoSrc={discord}
        description="Join the PSG discord to hang out with other gits, show off your gangs, share hobby stuff, etc..."
        action="Join Discord"
        event="psg_discord"
      />
      <SocialLink
        href="https://www.patreon.com/cw/planetsmashergames"
        logoSrc={patreon}
        description="Support Mike with cold hard cash so that he can keep building unreasonably fun games."
        action="View Patreon"
        event="psg_patreon"
      />
      <SocialLink
        href="https://www.youtube.com/@planetsmashergames"
        logoSrc={youtube}
        action="View Channel"
        description="Check out PSG on YouTube to peek behind the design process, watch learn-to-plays, and more."
        event="psg_youtube"
      />
    </div>
  );
}
